/**
 * Generic credential-collection modal for subscription-provider
 * integrations that DON'T auto-provision (RC and any future provider
 * declaring `credentials_fields` on /api/providers).
 *
 * Renders fields from `descriptor.credentials_fields` (label / type /
 * required), POSTs to `/api/integrations` on submit, optionally tests
 * the connection right after create (calls
 * `/api/integrations/{id}/test-connection`), and surfaces auth /
 * config errors inline.
 *
 * Used by IntegrationsTab.tsx — clicking "Connect" on a supported
 * tile that has no integration yet opens this modal.
 */

import { useState } from "react";
import { useDiscardGuard } from "@/lib/discard-guard";
import { Btn } from "@/components/Btn";
import {
  useCreateIntegration,
  useTestIntegrationConnection,
} from "@/lib/api-hooks";
import type { ProviderDescriptor } from "@/lib/types";

export function IntegrationConfigModal({
  descriptor,
  onClose: onCloseProp,
  onConnected,
}: {
  descriptor: ProviderDescriptor;
  onClose: () => void;
  onConnected: () => void;
}) {
  const create = useCreateIntegration();
  const testConn = useTestIntegrationConnection();
  // Each form field's value, keyed by `field.key`. All initially
  // empty strings — required fields are checked at submit.
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(descriptor.credentials_fields.map((f) => [f.key, ""])),
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const guard = useDiscardGuard(true, Object.values(values).some((v) => v.trim() !== ""), onCloseProp);
  const onClose = guard.requestClose;

  const missingRequired = descriptor.credentials_fields.some(
    (f) => f.required && !values[f.key].trim(),
  );

  const onSubmit = async () => {
    setError(null);
    setBusy(true);
    try {
      // Trim string values — RC dashboard often copies with trailing
      // whitespace and that breaks the Bearer header silently.
      const config: Record<string, string> = {};
      for (const [k, v] of Object.entries(values)) config[k] = v.trim();

      // Default to webhook mode — every provider we onboard via this
      // generic modal supports webhook ingestion. (Pull-only providers
      // would need a different UX.)
      const created = await create.mutateAsync({
        provider_code: descriptor.code,
        config,
        enabled_modes: descriptor.supported_modes.includes("webhook")
          ? ["webhook"]
          : descriptor.supported_modes,
      });

      // Smoke-test credentials immediately. Providers that don't
      // implement test_connection get a silent pass (BaseProvider's
      // default returns None). RC will hit the API v2 with a fake
      // customer_id and accept any 200/404 as OK.
      try {
        const result = await testConn.mutateAsync(created.id);
        if (!result.ok) {
          setError(result.message || "Connection test failed");
          // Leave the integration in place — user can retry test
          // from the row that just appeared, or fix creds via the
          // existing PATCH path. Surfacing the error inline beats
          // silently saving bad creds.
          return;
        }
      } catch (testErr) {
        // Network / unexpected test-connection error: treat as soft
        // failure, don't roll back. Backend will also surface this
        // via the next webhook attempt — we don't want a flaky
        // test-connection to block the user's Save.
        void testErr;
      }

      onConnected();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message ?? "Couldn't save integration";
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        background: "rgba(14,18,32,0.45)",
        display: "grid",
        placeItems: "center",
        padding: 20,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(440px, 96vw)",
          background: "var(--c-surface)",
          borderRadius: 12,
          boxShadow: "var(--sh-3)",
          padding: 24,
        }}
      >
        <h3 style={{ marginTop: 0, marginBottom: 8 }}>
          Connect {descriptor.display_name}
        </h3>
        <p
          className="muted"
          style={{ fontSize: 12.5, marginTop: 0, marginBottom: 18 }}
        >
          Enter the credentials below. The webhook URL will be shown after
          you save.
        </p>

        {descriptor.credentials_fields.map((f) => (
          <div className="field" key={f.key} style={{ marginBottom: 14 }}>
            <label className="label">
              {f.label}
              {f.required ? (
                <span style={{ color: "var(--c-neg-600)", marginLeft: 4 }}>
                  *
                </span>
              ) : null}
            </label>
            {f.type === "textarea" ? (
              <textarea
                className="input"
                rows={4}
                value={values[f.key]}
                onChange={(e) =>
                  setValues((v) => ({ ...v, [f.key]: e.target.value }))
                }
              />
            ) : (
              <input
                className="input"
                type={f.type === "secret" ? "password" : "text"}
                value={values[f.key]}
                autoComplete="off"
                spellCheck={false}
                onChange={(e) =>
                  setValues((v) => ({ ...v, [f.key]: e.target.value }))
                }
              />
            )}
          </div>
        ))}

        {error && (
          <div
            style={{
              fontSize: 12.5,
              color: "var(--c-neg-600)",
              background: "var(--c-neg-50, #ffeaea)",
              border: "1px solid var(--c-neg-200)",
              padding: "8px 10px",
              borderRadius: 8,
              marginBottom: 12,
            }}
          >
            {error}
          </div>
        )}

        <div
          style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}
        >
          <Btn onClick={onClose}>Cancel</Btn>
          <Btn
            variant="primary"
            onClick={() => void onSubmit()}
            disabled={busy || missingRequired}
          >
            {busy ? "Connecting…" : "Connect"}
          </Btn>
        </div>
      </div>
    </div>
  );
}
